import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { CheckCircle, ChevronRight } from 'lucide-react';
import GuestSignupCard from './GuestSignupCard.jsx';
import { trackConversionEvent } from '../../utils/conversionEvents.js';

/**
 * The /try guest gate: an anonymous visitor who has finished the free part
 * (Lesen, Sprachbausteine) signs up to continue. Shared by the digital
 * simulator and the paper exam, so both gate alike.
 *
 * `doneSectionName` is the part just finished, `nextSectionName` the one it
 * unlocks; `onGuestAuthed(user)` after an in-place sign-up, `onGuestGate()` for
 * the other sign-up options.
 */
const GuestGateModal = ({ examData, doneSectionName, nextSectionName, onGuestAuthed, onGuestGate }) => {
  const { t } = useTranslation();
  const [gateShowAuth, setGateShowAuth] = useState(false);
  return (
    <div
      className="fixed inset-0 z-[200] bg-black/40 backdrop-blur-sm flex items-center justify-center px-4 p-6"
      aria-modal="true"
      role="dialog"
      aria-label={t('tryExam.gate.title', { defaultValue: 'Unlock the full exam for free' })}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 280, damping: 26 }}
        className="relative w-full max-w-md bg-white border border-black/[0.05] shadow-[0_32px_64px_-16px_rgba(0,0,0,0.25)] rounded-2xl sm:rounded-3xl overflow-hidden"
      >
        <div className="flex flex-col items-center text-center px-6 pt-8 pb-7 gap-4">
          <div className="inline-flex items-center px-4 py-2 bg-emerald-100 text-emerald-800 rounded-full text-sm font-semibold">
            <CheckCircle className="w-4 h-4 mr-2" />
            {t('tryExam.gate.badge', { section: doneSectionName, defaultValue: '{{section}} complete' })}
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 leading-snug">
            {gateShowAuth
              ? t('tryExam.auth.title', { defaultValue: 'Create your free account' })
              : t('tryExam.gate.title', { defaultValue: 'Unlock the full exam for free' })}
          </h2>
          {!gateShowAuth && (
            <p className="text-gray-600 text-sm leading-relaxed">
              {t('tryExam.gate.text', { section: nextSectionName || '', defaultValue: 'Sign up to unlock {{section}} and every other part of the exam, see your complete results with explanations, and get AI feedback on your writing. Your free account takes a few seconds and you continue exactly where you left off.' })}
            </p>
          )}
          <p className="text-xs text-gray-400">
            {t('tryExam.gate.savedNote', { defaultValue: 'Your answers so far are saved on this device.' })}
          </p>
          {gateShowAuth ? (
            <GuestSignupCard onSuccess={onGuestAuthed} onMoreOptions={onGuestGate} />
          ) : (
            <button
              onClick={() => {
                trackConversionEvent('guest_exam_gate_signup_click', {
                  exam_provider: examData?.examProvider,
                  exam_level: examData?.level,
                });
                setGateShowAuth(true);
              }}
              className="w-full min-h-[48px] inline-flex items-center justify-center gap-2.5 px-8 py-3.5 rounded-xl bg-black text-white text-[15px] font-semibold shadow-lg hover:bg-gray-800 active:scale-[0.98] transition-all duration-200 cursor-pointer touch-manipulation"
            >
              {t('tryExam.gate.cta', { defaultValue: 'Sign up free & continue my exam' })}
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default GuestGateModal;
